import { useCallback } from "react";
import { Search } from "@smartrent/icons";

import {
  DataGrid,
  DataGridColumnDef,
  TableCellText,
  TableCellDate,
} from "@smartrent/ui";

import Layout from "@/layout/Layout";
import { useUser } from "@/layout/Context";

import { useDocumentTitle } from "@/hooks/use-document-title";
import { instance } from "@/lib/hooks";

import { OrganizationProps } from "@/types";
import { useDrawerNav } from "@/common/AppDrawer";

const columns: DataGridColumnDef<OrganizationProps>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: "Name",
    filter: {
      id: "name",
      type: "text",
      label: "Name",
      persistent: true,
      icon: Search,
      inputProps: {
        placeholder: "Search by name",
      },
    },
    enableSorting: true,
    size: 400,
    cell: TableCellText,
  },
  {
    id: "inserted_at",
    accessorKey: "inserted_at",
    header: "Created",
    enableSorting: true,
    cell: TableCellDate,
  },
];

export default function Organizations() {
  useDocumentTitle("Organizations");

  const { permissions } = useUser();
  const drawer = useDrawerNav();

  const getData = useCallback(
    async ({
      filters,
      pagination,
      sorting,
    }: {
      filters: { id: string; value: unknown }[];
      pagination: { pageIndex: number; pageSize: number };
      sorting: { id: string; desc: boolean }[];
    }) => {
      const params: Record<string, string> = {
        page: String(pagination.pageIndex + 1),
        limit: String(pagination.pageSize),
      };

      // Apply filters
      filters.forEach(({ id, value }) => {
        if (value != null && value !== "") {
          params[id] = String(value);
        }
      });

      // Apply sorting
      if (sorting.length > 0) {
        params.sort = sorting[0].id;
        params.dir = sorting[0].desc ? "desc" : "asc";
      }

      const queryString = new URLSearchParams(params).toString();
      const response = await instance.get(`/organizations?${queryString}`);
      return response.data;
    },
    []
  );

  const handleGetRowHref = useCallback(
    (row: { original: OrganizationProps }) =>
      `/organizations/${row.original.id}`,
    []
  );

  const handleCreateOrgButtonClick = useCallback(
    () => drawer.push("createOrganization", {}),
    [drawer]
  );

  return (
    <Layout>
      <DataGrid
        id="organizations"
        title="Organizations"
        columns={columns}
        getData={getData}
        getRowHref={handleGetRowHref}
        showPaginationControl
        showPageSizeControl
        initialPagination={{ pageIndex: 0, pageSize: 25, totalPages: 0 }}
        defaultPageSize={25}
        initialDensity="compact"
        showDensityControl={false}
        actionProps={
          permissions.update_organization
            ? {
                children: "Create",
                buttonProps: {
                  onPress: handleCreateOrgButtonClick,
                },
              }
            : undefined
        }
      />
    </Layout>
  );
}
